import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Upload, Check, Plus, ArrowRight, ArrowLeft, Sparkles, Loader2, MessageCircle, X,
         BookHeart, Truck, ShieldCheck, Trash2 } from "lucide-react";
import { toast, Toaster } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Slider } from "@/components/ui/slider";
import client, { fileToDataURL, mediaUrl } from "@/lib/api";
import { ROLES } from "@/lib/roles";
import { payForBook, PRICES } from "@/lib/pay";
import UpiPay from "@/components/UpiPay";
import { useAuth } from "@/context/AuthContext";
import Navbar from "@/components/Navbar";
import { TerryCorner, TerryCrossing } from "@/components/Terry";
import PhotoCropper from "@/components/PhotoCropper";
import MemoryJar from "@/components/MemoryJar";
import { Confetti } from "@/components/Decor";
import { personName, age as vAge, maxLen, required, firstError,
         shipping as validateShipping, phone as vPhone } from "@/lib/validate";

const STEPS = ["Child", "Cast", "Theme", "Style", "Pages", "Checkout", "Done"];

// Terry keeps you company through the wizard, in the pose the design boards
// give each step: peering at the photos, weighing up the worlds, then reading
// the finished thing. Indexed by step, so it stays in step with STEPS.
const TERRY_POSE = ["curious", "curious", "thinking", "thinking", "reading", "heart", "hero"];

/** The meadow path across the top of the wizard. The walking creature marks
    where you are; the trail behind it fills in as you go. */
function StepTrail({ step }) {
    const last = STEPS.length - 1;
    const pct = (Math.min(step, last) / last) * 100;
    return (
        <div className="relative h-[78px]" data-testid="wizard-trail">
            <div className="absolute top-11 left-0 right-0 h-1.5 rounded-full bg-plum/10" />
            <div className="absolute top-11 left-0 h-1.5 rounded-full bg-bubblegum transition-[width] duration-500 ease-out"
                 style={{ width: `${pct}%` }} />
            <div className="absolute top-[38px] left-0 right-0 flex justify-between">
                {STEPS.map((label, i) => (
                    <div key={label} className="flex flex-col items-center gap-2 w-[52px] sm:w-[72px]">
                        <div className={`w-[18px] h-[18px] rounded-full border-[3px] border-paper transition-colors
                                         ${i <= step ? "bg-bubblegum" : "bg-plum/20"}`} />
                        <div className="font-display text-[10px] sm:text-xs text-plum">{label}</div>
                    </div>
                ))}
            </div>
            {/* The creature */}
            <div className="absolute top-0 w-[46px] transition-[left]"
                 style={{
                     left: `calc(${pct}% - ${(pct / 100) * 46}px)`,
                     transitionDuration: "560ms",
                     transitionTimingFunction: "cubic-bezier(.34,1.4,.64,1)",
                 }}
                 aria-hidden>
                <div className="relative w-[46px] h-[34px] rounded-t-[23px] rounded-b-[18px] bg-lavender shadow-[0_6px_14px_rgba(45,27,78,0.2)] anim-bob"
                     style={{ animationDuration: "2.6s" }}>
                    <div className="absolute -top-[9px] left-2 w-2.5 h-3.5 rounded-full bg-lavender" />
                    <div className="absolute -top-[9px] right-2 w-2.5 h-3.5 rounded-full bg-lavender" />
                    <div className="absolute top-[13px] left-3 w-[5px] h-[5px] rounded-full bg-plum" />
                    <div className="absolute top-[13px] right-3 w-[5px] h-[5px] rounded-full bg-plum" />
                </div>
            </div>
        </div>
    );
}

export default function Wizard() {
    const { user } = useAuth();
    const nav = useNavigate();
    const [sp] = useSearchParams();
    const [step, setStep] = useState(0);
    const [method, setMethod] = useState("razorpay");
    const [upiOrder, setUpiOrder] = useState(null);
    // Child
    const [child, setChild] = useState({ name: "", age: "", personality: "", photo_b64: "" });
    // Cast
    const [cast, setCast] = useState([]); // array of { role, role_label, name, photo_b64, id? }
    const [openRole, setOpenRole] = useState(null);
    const [castDraft, setCastDraft] = useState({ role: "", role_label: "", name: "", photo_b64: "" });
    // Theme
    const [themes, setThemes] = useState([]);
    const [themesError, setThemesError] = useState(false);
    const [themeKey, setThemeKey] = useState("forest");
    const [customTheme, setCustomTheme] = useState("");
    // Real moments woven into this book. The theme is only the world it's drawn in.
    const [memoryIds, setMemoryIds] = useState([]);
    // Style
    const [styles, setStyles] = useState([]);
    const [artStyle, setArtStyle] = useState("photoreal");
    const [textInImage, setTextInImage] = useState(true);
    // Pages
    const [pageCount, setPageCount] = useState(10);
    // Book
    const [book, setBook] = useState(null);
    const [feedback, setFeedback] = useState("");
    const [busy, setBusy] = useState(false);
    const [pollError, setPollError] = useState(null);
    // Saved characters (from previous stories) for reuse
    const [savedChildren, setSavedChildren] = useState([]);
    const [savedCast, setSavedCast] = useState([]);

    useEffect(() => {
        if (!user) nav("/auth");
    }, [user, nav]);

    useEffect(() => {
        loadThemes();
        client.get("/styles").then(({ data }) => {
            setStyles(data.styles);
            setArtStyle(data.default);
        }).catch(() => {});
        client.get("/characters").then(({ data }) => {
            const chars = data.characters || [];
            setSavedChildren(chars.filter((c) => c.role === "child"));
            setSavedCast(chars.filter((c) => c.role !== "child"));
        }).catch(() => {});
    }, []);

    // Resuming an unpaid draft. Without this, "Finish checkout" dropped people at
    // step 0 with an empty wizard and they had to rebuild the whole cast.
    useEffect(() => {
        const bid = sp.get("book");
        if (!bid) return;
        (async () => {
            const [{ data: bk }, { data: ch }] = await Promise.all([
                client.get(`/books/${bid}`),
                client.get("/characters"),
            ]);
            const chars = ch.characters || [];
            const kid = chars.find((c) => c.id === bk.child_id);
            if (kid) setChild({ ...kid, age: kid.age || "", personality: kid.personality || "", existing: true });
            setCast(chars.filter((c) => (bk.cast_ids || []).includes(c.id)));
            setThemeKey(bk.theme_key || "forest");
            setCustomTheme(bk.custom_theme || "");
            setMemoryIds(bk.memory_ids || []);
            setPageCount(bk.page_count || 10);
            if (bk.art_style) setArtStyle(bk.art_style);
            setTextInImage(bk.text_in_image !== false);
            setBook(bk);
            setStep(bk.status === "draft" ? 5 : 6);
        })().catch(() => toast.error("Could not open that draft — start a new story instead."));
    }, [sp]);

    const loadThemes = () => {
        client.get("/themes")
            .then(({ data }) => { setThemes(data.themes); setThemesError(false); })
            .catch(() => setThemesError(true));
    };

    const pickSavedChild = (c) => {
        setChild({
            id: c.id, name: c.name, age: c.age || "",
            personality: c.personality || "", photo_b64: c.photo_b64,
            existing: true,
        });
    };

    const addSavedCast = (c) => {
        if (cast.some((x) => x.id === c.id)) return;
        setCast([...cast, c]);
    };

    // Photos of children, stored indefinitely with no way to remove them, is not
    // a defensible default for this product. Delivered books keep their artwork —
    // only the source photo and its reuse card go.
    const forgetCharacter = async (c) => {
        if (!window.confirm(
            `Remove ${c.name} from your library? Books already made keep their artwork, ` +
            `but the photo is deleted and can't be reused.`)) return;
        try {
            await client.delete(`/characters/${c.id}`);
            setSavedChildren((prev) => prev.filter((x) => x.id !== c.id));
            setSavedCast((prev) => prev.filter((x) => x.id !== c.id));
            setCast((prev) => prev.filter((x) => x.id !== c.id));
            // If they were mid-wizard with this exact character, drop the link to
            // the row that no longer exists rather than posting a dead id.
            setChild((prev) => (prev.id === c.id
                ? { ...prev, id: undefined, existing: false } : prev));
            toast.success(`${c.name} removed from your library.`);
        } catch {
            toast.error("Couldn't remove that — please try again.");
        }
    };

    const lockedRoles = useMemo(() => new Set(cast.map((c) => c.role === "other" ? `other:${c.role_label}` : c.role)), [cast]);

    // ---- Step 1: Child
    const submitChild = async () => {
        const bad = firstError([
            personName(child.name),
            vAge(child.age),
            maxLen(child.personality, 500, "Personality notes"),
            child.photo_b64 ? null : "Add a photo",
        ]);
        if (bad) { toast.error(bad); return; }
        if (child.existing && child.id) { setStep(1); return; }
        setBusy(true);
        try {
            const { data } = await client.post("/characters", { role: "child", name: child.name,
                age: Number(child.age) || null, personality: child.personality, photo_b64: child.photo_b64 });
            setChild({ ...child, id: data.id });
            setStep(1);
        } catch (e) { toast.error("Could not save the child"); } finally { setBusy(false); }
    };

    // ---- Step 2: Cast
    const openRoleModal = (role) => {
        setCastDraft({ role: role.key, role_label: role.key === "other" ? "" : role.label, name: "", photo_b64: "" });
        setOpenRole(role);
    };
    const saveCast = async () => {
        const bad = firstError([
            personName(castDraft.name, "Their name"),
            castDraft.role === "other"
                ? required(castDraft.role_label, "Relationship label") ||
                  maxLen(castDraft.role_label, 120, "Relationship label")
                : null,
            castDraft.photo_b64 ? null : "Add a photo",
        ]);
        if (bad) { toast.error(bad); return; }
        setBusy(true);
        try {
            const { data } = await client.post("/characters", castDraft);
            setCast([...cast, data]);
            setOpenRole(null);
        } catch (e) { toast.error("Could not save"); } finally { setBusy(false); }
    };

    // ---- Step 3-4 -> save the brief, then take payment
    const startGeneration = async () => {
        // "custom" is the one theme with no prompt of its own — the description
        // is the brief, so an empty one buys a book nobody can illustrate.
        if (themeKey === "custom") {
            const bad = firstError([
                required(customTheme, "A story description"),
                maxLen(customTheme, 500, "Story description"),
            ]);
            if (bad) { toast.error(bad); return; }
        }
        setBusy(true);
        try {
            const { data } = await client.post("/books", {
                child_id: child.id, cast_ids: cast.map((c) => c.id),
                memory_ids: memoryIds,
                theme_key: themeKey, custom_theme: themeKey === "custom" ? customTheme : null,
                page_count: pageCount, art_style: artStyle, text_in_image: textInImage,
            });
            setBook(data);
            setStep(5);
            // A draft means nothing is illustrated until they pay; the legacy AI
            // path returns "generating" and still wants polling.
            if (data.status !== "draft") poll(data.id);
        } catch (e) { toast.error("Could not save your story brief"); } finally { setBusy(false); }
    };

    const checkout = async (kind, shipping) => {
        setBusy(true);
        try {
            const order = await payForBook({
                bookId: book.id, kind, method, shipping,
                title: `${child.name}'s storybook`,
                contact: { name: user?.name, email: user?.email, phone: user?.phone },
            });
            // UPI unlocks nothing until an admin confirms the credit, so the
            // dialog takes over from here rather than the book refreshing.
            if (order.upi) { setUpiOrder(order); return; }
            const { data } = await client.get(`/books/${book.id}`);
            setBook(data);
            setStep(6);
        } catch (e) {
            if (e?.cancelled) toast("Payment cancelled — your story brief is saved.");
            else toast.error(e?.response?.data?.detail || e?.message || "Payment failed");
        } finally { setBusy(false); }
    };

    const poll = (bid) => {
        let cancelled = false;
        let fails = 0;
        const tick = async () => {
            if (cancelled) return;
            try {
                const { data } = await client.get(`/books/${bid}`);
                fails = 0;
                setPollError(null);
                setBook(data);
                if (data.status === "ready" || data.status === "finalized") {
                    setStep(6);
                    return;
                }
                if (data.status === "failed") {
                    toast.error(data.error || "Generation failed");
                    setPollError("Generation failed. Please try again.");
                    return;
                }
                setTimeout(tick, 2000);
            } catch (e) {
                // Silently retrying forever just looks like a hang — say something.
                fails += 1;
                if (fails >= 3) {
                    setPollError(
                        e?.response?.status === 500
                            ? "We can't reach the story server right now. Your book is safe — this page will pick up again automatically."
                            : "Lost connection to the story server. Retrying…"
                    );
                }
                setTimeout(tick, 3000);
            }
        };
        tick();
        return () => { cancelled = true; };
    };

    const submitFeedback = async () => {
        const bad = firstError([
            required(feedback, "Feedback"),
            maxLen(feedback, 2000, "Feedback"),
        ]);
        if (bad) { toast.error(bad); return; }
        setBusy(true);
        try {
            const { data } = await client.post(`/books/${book.id}/feedback`, { feedback });
            setFeedback("");
            setStep(5);
            poll(book.id);
            toast.success(`Feedback round ${data.rounds_used} of 3`);
        } catch (e) { toast.error(e?.response?.data?.detail || "Could not submit feedback"); } finally { setBusy(false); }
    };

    const finalize = async () => {
        setBusy(true);
        try {
            await client.post(`/books/${book.id}/finalize`);
            nav(`/read/${book.id}`);
        } finally { setBusy(false); }
    };

    return (
        <div className="min-h-screen magic-bg">
            <Toaster position="top-center" />
            <Navbar />
            {/* On the theme step he stands to the left, out of the way of the
                grid of worlds — the same side the design board puts him on. */}
            <TerryCorner pose={TERRY_POSE[Math.min(step, TERRY_POSE.length - 1)]}
                         side={step === 2 ? "left" : "right"} />

            {/* Progress trail — a little creature walks the meadow path as you go. */}
            <div className="max-w-4xl mx-auto px-6 pt-8">
                <StepTrail step={step} />
                <div className="mt-3 text-xs tracking-[0.28em] uppercase text-plum/60 text-center" data-testid="wizard-step-label">
                    Step {Math.min(step + 1, STEPS.length)} of {STEPS.length} — {STEPS[Math.min(step, STEPS.length - 1)]}
                </div>
            </div>

            <div className="max-w-4xl mx-auto px-6 py-10">
                <AnimatePresence mode="wait">
                    {step === 0 && (
                        <motion.div key="child" initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -14 }}>
                            <h1 className="font-display text-4xl tracking-tight mb-2">Meet the star of the story.</h1>
                            <div className="script-note text-2xl mb-1">one clear face photo — that is all we need</div>
                            <p className="text-plum/60 mb-8">Upload a clear photo of your child, and tell us a little about them.</p>

                            {savedChildren.length > 0 && (
                                <div className="mb-6" data-testid="saved-children">
                                    <div className="text-xs tracking-[0.2em] uppercase text-plum/60 mb-2 flex items-center gap-2">
                                        <Sparkles className="w-3.5 h-3.5" /> Bring back a hero from your library
                                    </div>
                                    <div className="flex gap-3 overflow-x-auto pb-2">
                                        {savedChildren.map((c) => (
                                            <div key={c.id} className="relative shrink-0 group">
                                                <button onClick={() => pickSavedChild(c)}
                                                    className={`card-cloud w-32 text-center overflow-hidden p-0 hover:scale-[1.03] transition-transform ${child.id === c.id ? "ring-4 ring-bubblegum" : ""}`}
                                                    data-testid={`saved-child-${c.id}`}>
                                                    <img src={c.photo_b64} className="w-full h-32 object-cover" alt="" />
                                                    <div className="p-2 text-sm font-semibold text-plum truncate">{c.name}</div>
                                                </button>
                                                <button onClick={() => forgetCharacter(c)}
                                                    aria-label={`Remove ${c.name} from your library`}
                                                    data-testid={`forget-child-${c.id}`}
                                                    className="absolute top-1.5 right-1.5 w-7 h-7 rounded-full bg-white/90 grid place-items-center
                                                               opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity shadow-sm">
                                                    <Trash2 className="w-3.5 h-3.5 text-bubblegum" />
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            <div className="card-cloud p-8 grid md:grid-cols-2 gap-8">
                                <PhotoUpload value={child.photo_b64} onChange={(b64) => setChild({ ...child, photo_b64: b64, existing: false, id: undefined })} testId="child-photo" />
                                <div className="space-y-4">
                                    <div>
                                        <Label>Name</Label>
                                        <Input value={child.name} onChange={(e) => setChild({ ...child, name: e.target.value })}
                                            placeholder="Aarav" maxLength={60} className="mt-1.5 bg-white" data-testid="child-name-input" />
                                    </div>
                                    <div>
                                        <Label>Age</Label>
                                        <Input value={child.age} onChange={(e) => setChild({ ...child, age: e.target.value })}
                                            placeholder="5" type="number" min={0} max={120} className="mt-1.5 bg-white" data-testid="child-age-input" />
                                    </div>
                                    <div>
                                        <Label>A few personality notes</Label>
                                        <Textarea value={child.personality} onChange={(e) => setChild({ ...child, personality: e.target.value })}
                                            placeholder="Loves dinosaurs, brave, always curious." rows={3} maxLength={500}
                                            className="mt-1.5 bg-white" data-testid="child-personality-input" />
                                    </div>
                                </div>
                            </div>
                            <div className="flex justify-end mt-6">
                                <Button size="lg" className="rounded-full bg-bubblegum hover:bg-bubblegum/90"
                                    onClick={submitChild} disabled={busy} data-testid="child-next-btn">
                                    Continue <ArrowRight className="w-4 h-4 ml-1" />
                                </Button>
                            </div>
                        </motion.div>
                    )}

                    {step === 1 && (
                        <motion.div key="cast" initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -14 }}>
                            <h1 className="font-display text-4xl tracking-tight mb-2">Build Your Cast</h1>
                            <div className="script-note text-2xl mb-1">everyone your child would want in the story</div>
                            <p className="text-plum/60 mb-8">Add loved ones one at a time. Their face — and their name — stays consistent throughout the story.</p>
                            {cast.length > 0 && (
                                <div className="mb-6 flex flex-wrap gap-3" data-testid="cast-added-list">
                                    {cast.map((c) => (
                                        <div key={c.id} className="card-cloud px-3 py-2 flex items-center gap-2">
                                            <img src={c.photo_b64} className="w-8 h-8 rounded-full object-cover" alt="" />
                                            <div className="text-sm"><b>{c.name}</b> <span className="text-plum/50">as {c.role_label || c.role}</span></div>
                                            <button onClick={() => setCast(cast.filter((x) => x.id !== c.id))} className="text-plum/40 hover:text-plum ml-1" data-testid={`cast-remove-${c.id}`}>
                                                <X className="w-3.5 h-3.5" />
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            )}

                            {savedCast.filter((c) => !cast.some((x) => x.id === c.id)).length > 0 && (
                                <div className="mb-6" data-testid="saved-cast">
                                    <div className="text-xs tracking-[0.2em] uppercase text-plum/60 mb-2 flex items-center gap-2">
                                        <Sparkles className="w-3.5 h-3.5" /> Bring back from your library
                                    </div>
                                    <div className="flex gap-3 overflow-x-auto pb-2">
                                        {savedCast.filter((c) => !cast.some((x) => x.id === c.id)).map((c) => {
                                            const roleBlocked = c.role !== "other" && lockedRoles.has(c.role);
                                            return (
                                                <div key={c.id} className="relative shrink-0 group">
                                                    <button onClick={() => !roleBlocked && addSavedCast(c)} disabled={roleBlocked}
                                                        className={`card-cloud w-28 text-center overflow-hidden p-0 hover:scale-[1.03] transition-transform ${roleBlocked ? "opacity-40 cursor-not-allowed" : ""}`}
                                                        data-testid={`saved-cast-${c.id}`}>
                                                        <img src={c.photo_b64} className="w-full h-28 object-cover" alt="" />
                                                        <div className="p-2 text-xs font-semibold text-plum truncate">{c.name}</div>
                                                        <div className="text-[10px] text-plum/50 pb-2 truncate">{c.role_label || c.role}</div>
                                                    </button>
                                                    <button onClick={() => forgetCharacter(c)}
                                                        aria-label={`Remove ${c.name} from your library`}
                                                        data-testid={`forget-cast-${c.id}`}
                                                        className="absolute top-1.5 right-1.5 w-7 h-7 rounded-full bg-white/90 grid place-items-center
                                                                   opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity shadow-sm">
                                                        <Trash2 className="w-3.5 h-3.5 text-bubblegum" />
                                                    </button>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}

                            <div className="text-xs tracking-[0.2em] uppercase text-plum/60 mb-3 flex items-center gap-2">
                                <Plus className="w-3.5 h-3.5" /> Or add a new loved one
                            </div>
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                                {ROLES.map((r) => {
                                    const locked = r.key !== "other" && lockedRoles.has(r.key);
                                    return (
                                        <button key={r.key} disabled={locked} onClick={() => openRoleModal(r)}
                                            className={`card-cloud aspect-square flex flex-col items-center justify-center gap-2 p-4 transition-transform hover:scale-[1.02] ${locked ? "opacity-40 cursor-not-allowed" : ""}`}
                                            data-testid={`role-${r.key}`}>
                                            <div className="w-10 h-10 rounded-full bg-lavender/50 grid place-items-center text-bubblegum">
                                                {locked ? <Check className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
                                            </div>
                                            <span className="text-sm font-medium text-center">{r.label}</span>
                                        </button>
                                    );
                                })}
                            </div>
                            <div className="flex justify-between mt-8">
                                <Button variant="ghost" onClick={() => setStep(0)} data-testid="cast-back-btn"><ArrowLeft className="w-4 h-4 mr-1" /> Back</Button>
                                <Button size="lg" className="rounded-full bg-bubblegum hover:bg-bubblegum/90"
                                    onClick={() => setStep(2)} data-testid="cast-next-btn">
                                    Continue with {cast.length} loved one{cast.length !== 1 ? "s" : ""} <ArrowRight className="w-4 h-4 ml-1" />
                                </Button>
                            </div>

                            {openRole && (
                                <div className="fixed inset-0 z-50 bg-ink/40 backdrop-blur-sm grid place-items-center p-6">
                                    <div className="card-cloud max-w-md w-full p-7 relative">
                                        <button onClick={() => setOpenRole(null)} className="absolute top-4 right-4 text-plum/50 hover:text-plum" data-testid="role-modal-close">
                                            <X className="w-4 h-4" />
                                        </button>
                                        <div className="text-xs tracking-[0.28em] uppercase text-plum/60 mb-2">Add</div>
                                        <h3 className="font-display text-2xl mb-6">{openRole.label}</h3>
                                        <div className="space-y-4">
                                            {openRole.key === "other" && (
                                                <div>
                                                    <Label>Relationship label</Label>
                                                    <Input value={castDraft.role_label} onChange={(e) => setCastDraft({ ...castDraft, role_label: e.target.value })}
                                                        placeholder="Godfather" maxLength={120} className="mt-1.5 bg-white" data-testid="cast-custom-label-input" />
                                                </div>
                                            )}
                                            <div>
                                                <Label>Their name</Label>
                                                <Input value={castDraft.name} onChange={(e) => setCastDraft({ ...castDraft, name: e.target.value })}
                                                    placeholder="Ravi" maxLength={60} className="mt-1.5 bg-white" data-testid="cast-name-input" />
                                            </div>
                                            <PhotoUpload value={castDraft.photo_b64} onChange={(b64) => setCastDraft({ ...castDraft, photo_b64: b64 })} testId="cast-photo" />
                                            <Button onClick={saveCast} disabled={busy}
                                                className="w-full rounded-full bg-bubblegum hover:bg-bubblegum/90" data-testid="cast-save-btn">
                                                {busy ? "Saving…" : "Add to cast"}
                                            </Button>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </motion.div>
                    )}

                    {step === 2 && themes.length === 0 && (
                        <motion.div key="theme-empty" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                            <h1 className="font-display text-4xl tracking-tight mb-2">Choose a story world.</h1>
                            <div className="script-note text-2xl mb-1">tap one — you can change it later</div>
                            <div className="card-cloud p-8 text-center text-plum/60 mt-6" data-testid="themes-empty">
                                {themesError
                                    ? "We couldn't reach the story collection just now."
                                    : "Opening the story collection…"}
                                <div className="mt-4">
                                    <Button onClick={loadThemes} variant="outline" className="rounded-full"
                                        data-testid="themes-retry">Try again</Button>
                                </div>
                            </div>
                            <div className="flex justify-start mt-8">
                                <Button variant="ghost" onClick={() => setStep(1)}><ArrowLeft className="w-4 h-4 mr-1" /> Back</Button>
                            </div>
                        </motion.div>
                    )}

                    {step === 2 && themes.length > 0 && (
                        <motion.div key="theme" initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -14 }}>
                            <h1 className="font-display text-4xl tracking-tight mb-2">Choose a story world.</h1>
                            <div className="script-note text-2xl mb-1">tap one — you can change it later</div>
                            <p className="text-plum/60 mb-8">Pick one from The Collection, or write your own.</p>
                            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                                {themes.map((t) => (
                                    <button key={t.key} onClick={() => setThemeKey(t.key)}
                                        className={`card-cloud text-left overflow-hidden transition-all hover:scale-[1.02] p-0 ${themeKey === t.key ? "ring-4 ring-bubblegum" : ""}`}
                                        data-testid={`theme-${t.key}`}>
                                        <div className="aspect-[4/3] relative bg-gradient-to-br from-rose to-buttercup overflow-hidden">
                                            {t.cover_url && (
                                                <img src={mediaUrl(t.cover_url)} alt={t.name} className="absolute inset-0 w-full h-full object-cover" draggable={false} />
                                            )}
                                            {themeKey === t.key && (
                                                <div className="absolute top-2 right-2 w-7 h-7 rounded-full bg-bubblegum text-white grid place-items-center shadow-md">
                                                    <Check className="w-4 h-4" />
                                                </div>
                                            )}
                                        </div>
                                        <div className="p-4">
                                            <div className="text-xs tracking-[0.2em] uppercase text-plum/60">Theme</div>
                                            <div className="font-display text-xl mt-1 text-plum">{t.name}</div>
                                            <div className="text-sm text-plum/60 mt-1">{t.blurb}</div>
                                        </div>
                                    </button>
                                ))}
                            </div>
                            {themeKey === "custom" && (
                                <div className="mt-6">
                                    <Label>Describe your story idea (any words work)</Label>
                                    <Textarea value={customTheme} onChange={(e) => setCustomTheme(e.target.value)}
                                        rows={4} maxLength={500} className="mt-1.5 bg-white"
                                        placeholder="A story where Aarav helps Grandma find her lost eyeglasses in a rainy Mumbai market…"
                                        data-testid="custom-theme-input" />
                                </div>
                            )}

                            <div className="mt-12 pt-10 border-t border-plum/10">
                                <h2 className="font-display text-3xl tracking-tight mb-1">
                                    Now put something real in it.
                                </h2>
                                <div className="script-note text-2xl mb-1">this is the part only you have</div>
                                <p className="text-plum/60 mb-6 max-w-2xl">
                                    Tick the moments that actually happened and they become the pages —
                                    the world above is only what it's drawn in. Skip this and you get a
                                    lovely story; keep one and you get <em>yours</em>.
                                </p>
                                <MemoryJar selectable selected={memoryIds} onSelect={setMemoryIds} limit={12} />
                            </div>

                            <div className="flex justify-between mt-8">
                                <Button variant="ghost" onClick={() => setStep(1)}><ArrowLeft className="w-4 h-4 mr-1" /> Back</Button>
                                <Button size="lg" className="rounded-full bg-bubblegum hover:bg-bubblegum/90"
                                    onClick={() => setStep(3)} data-testid="theme-next-btn">
                                    Continue <ArrowRight className="w-4 h-4 ml-1" />
                                </Button>
                            </div>
                        </motion.div>
                    )}

                    {step === 3 && (
                        <motion.div key="style" initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -14 }}>
                            <h1 className="font-display text-4xl tracking-tight mb-2">How should it look?</h1>
                            <div className="script-note text-2xl mb-1">pick the drawing style you love</div>
                            <p className="text-plum/60 mb-8">Every page is drawn in the style you pick — and your faces stay the same throughout.</p>
                            <div className="grid grid-cols-2 md:grid-cols-3 gap-4" data-testid="style-grid">
                                {styles.map((st) => (
                                    <button key={st.key} onClick={() => setArtStyle(st.key)}
                                        className={`card-cloud text-left overflow-hidden p-0 transition-all hover:scale-[1.02] ${artStyle === st.key ? "ring-4 ring-bubblegum" : ""}`}
                                        data-testid={`style-${st.key}`}>
                                        {/* Picking a style from a blurb alone is guesswork. The cover is
                                            the same scene in every style, so they compare like for like.
                                            Missing file: onError hides it and the text card still works. */}
                                        <div className="relative aspect-[4/3] bg-lavender/40">
                                            <img src={st.cover_url} alt="" loading="lazy" draggable={false}
                                                onError={(e) => { e.currentTarget.style.display = "none"; }}
                                                className="w-full h-full object-cover select-none" />
                                            {artStyle === st.key && (
                                                <div className="absolute top-2 right-2 w-6 h-6 rounded-full bg-bubblegum text-white grid place-items-center shadow">
                                                    <Check className="w-3.5 h-3.5" />
                                                </div>
                                            )}
                                        </div>
                                        <div className="p-4">
                                            <div className="font-display text-xl text-plum">{st.name}</div>
                                            <div className="text-sm text-plum/60 mt-1">{st.blurb}</div>
                                            {st.note && (
                                                <div className="text-xs text-amber-700/80 mt-2">{st.note}</div>
                                            )}
                                        </div>
                                    </button>
                                ))}
                            </div>

                            <button onClick={() => setTextInImage(!textInImage)}
                                className="card-cloud w-full mt-6 p-5 flex items-center gap-4 text-left hover:scale-[1.01] transition-transform"
                                data-testid="text-in-image-toggle">
                                <div className={`w-12 h-7 rounded-full shrink-0 transition-colors relative ${textInImage ? "bg-bubblegum" : "bg-border"}`}>
                                    <div className={`w-5 h-5 rounded-full bg-white absolute top-1 transition-all ${textInImage ? "left-6" : "left-1"}`} />
                                </div>
                                <div>
                                    <div className="font-display text-lg text-plum">Letter the words onto the page</div>
                                    <div className="text-sm text-plum/60">
                                        The story text is hand-lettered into each illustration on a little storybook card,
                                        just like a printed picture book. Turn this off for plain artwork with the text below it.
                                    </div>
                                </div>
                            </button>

                            <div className="flex justify-between mt-8">
                                <Button variant="ghost" onClick={() => setStep(2)}><ArrowLeft className="w-4 h-4 mr-1" /> Back</Button>
                                <Button size="lg" className="rounded-full bg-bubblegum hover:bg-bubblegum/90"
                                    onClick={() => setStep(4)} data-testid="style-next-btn">
                                    Continue <ArrowRight className="w-4 h-4 ml-1" />
                                </Button>
                            </div>
                        </motion.div>
                    )}

                    {step === 4 && (
                        <motion.div key="pages" initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -14 }}>
                            <h1 className="font-display text-4xl tracking-tight mb-2">How many pages?</h1>
                            <div className="script-note text-2xl mb-1">twelve is the bedtime sweet spot</div>
                            <p className="text-plum/60 mb-8">This count includes your cover and closing page.</p>
                            <div className="card-cloud p-8">
                                <div className="text-center mb-6">
                                    <div className="font-display text-7xl text-bubblegum" data-testid="page-count-value">{pageCount}</div>
                                    <div className="text-sm text-plum/50 mt-2">pages, cover to close</div>
                                </div>
                                <Slider value={[pageCount]} min={8} max={16} step={1} onValueChange={(v) => setPageCount(v[0])}
                                    data-testid="page-count-slider" />
                                <div className="flex justify-between text-xs text-plum/50 mt-2">
                                    <span>8 pages</span><span>16 pages</span>
                                </div>
                            </div>
                            <div className="flex justify-between mt-8">
                                <Button variant="ghost" onClick={() => setStep(3)}><ArrowLeft className="w-4 h-4 mr-1" /> Back</Button>
                                <Button size="lg" className="rounded-full bg-bubblegum hover:bg-bubblegum/90"
                                    onClick={startGeneration} disabled={busy} data-testid="start-generation-btn">
                                    <Sparkles className="w-4 h-4 mr-1" /> Create the magic
                                </Button>
                            </div>
                        </motion.div>
                    )}

                    {step === 5 && book?.status === "draft" && (
                        <motion.div key="checkout" initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }}>
                            <Checkout child={child} cast={cast} themes={themes} themeKey={themeKey}
                                customTheme={customTheme} artStyle={artStyle} pageCount={pageCount}
                                textInImage={textInImage} busy={busy}
                                method={method} setMethod={setMethod}
                                onPay={checkout} onBack={() => setStep(4)} />
                        </motion.div>
                    )}

                    {step === 5 && book && book.status !== "draft" && (
                        <motion.div key="loading" initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-center py-20">
                            <GenerationLoader book={book} error={pollError} />
                        </motion.div>
                    )}

                    {step === 6 && book && !book.purchased && !book.pages?.length && (
                        <motion.div key="upi-pending" initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }}>
                            <UpiPending childName={child.name} onLibrary={() => nav("/library")} />
                        </motion.div>
                    )}

                    {step === 6 && book?.purchased && !book.pages?.length && (
                        <motion.div key="ordered" initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }}>
                            <OrderPlaced book={book} childName={child.name} onLibrary={() => nav("/library")} />
                        </motion.div>
                    )}

                    {step === 6 && book && book.pages?.length > 0 && (
                        <motion.div key="preview" initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }}>
                            <div className="mb-6">
                                <div className="text-xs tracking-[0.28em] uppercase text-plum/60 mb-2">Story Preview</div>
                                <h1 className="font-display text-4xl tracking-tight">{book.title}</h1>
                            </div>

                            <PreviewGrid pages={book.pages} showCaptions={!book.text_in_image} />

                            <div className="card-cloud p-6 mt-8" data-testid="feedback-panel">
                                <div className="flex items-center justify-between mb-3">
                                    <div className="font-display text-xl flex items-center gap-2">
                                        <MessageCircle className="w-5 h-5 text-bubblegum" /> Fine-Tune Your Story
                                    </div>
                                    <div className="text-xs text-plum/50">
                                        Feedback round {book.feedback_rounds_used} of 3
                                    </div>
                                </div>
                                {book.feedback_rounds_used < 3 ? (
                                    <>
                                        <Textarea value={feedback} onChange={(e) => setFeedback(e.target.value)}
                                            rows={3} maxLength={2000} placeholder="Make the ending softer, add more about Grandma's saree…"
                                            className="bg-white" data-testid="feedback-input" />
                                        <div className="flex justify-end mt-3">
                                            <Button onClick={submitFeedback} disabled={busy}
                                                variant="outline" className="rounded-full" data-testid="feedback-submit-btn">
                                                {busy ? "Rewriting…" : "Rewrite with this feedback"}
                                            </Button>
                                        </div>
                                    </>
                                ) : (
                                    <div className="text-sm text-plum/50">You've used all 3 feedback rounds. Ready to finalise your story.</div>
                                )}
                            </div>

                            <div className="flex justify-end mt-8">
                                <Button size="lg" className="rounded-full bg-bubblegum hover:bg-bubblegum/90"
                                    onClick={finalize} disabled={busy} data-testid="finalize-btn">
                                    Finalise & open the book <ArrowRight className="w-4 h-4 ml-1" />
                                </Button>
                            </div>
                        </motion.div>
                    )}
                </AnimatePresence>
            </div>

            <UpiPay order={upiOrder} open={!!upiOrder}
                onOpenChange={(o) => { if (!o) setUpiOrder(null); }}
                onClaimed={() => { setUpiOrder(null); setStep(6); }} />
        </div>
    );
}

function PhotoUpload({ value, onChange, testId }) {
    // Every picked photo goes through the cropper before it is accepted — the
    // illustrator works from a square face, and a phone camera roll is full of
    // wide shots where the child is a speck in the corner.
    const [pending, setPending] = useState(null);
    return (
        <>
            <label className={`card-cloud aspect-square grid place-items-center overflow-hidden cursor-pointer relative ${value ? "" : "border-2 border-dashed"}`}
                data-testid={`${testId}-upload`}>
                {value ? (
                    <img src={value} className="w-full h-full object-cover" alt="" />
                ) : (
                    <div className="text-center px-6">
                        <Upload className="w-6 h-6 mx-auto text-bubblegum mb-2" />
                        <div className="text-sm font-medium">Upload a photo</div>
                        <div className="text-xs text-plum/50 mt-1">Clear, well-lit face works best</div>
                    </div>
                )}
                <input type="file" accept="image/*" className="hidden"
                    onChange={async (e) => {
                        const f = e.target.files?.[0];
                        e.target.value = "";   // so re-picking the same file still fires
                        if (!f) return;
                        try { setPending(await fileToDataURL(f)); }
                        catch (err) { toast.error(err.message); }
                    }} />
            </label>
            {value && (
                <button type="button" onClick={() => setPending(value)}
                    className="mt-2 text-xs text-bubblegum font-semibold hover:underline"
                    data-testid={`${testId}-recrop`}>
                    Adjust the crop
                </button>
            )}
            {pending && (
                <PhotoCropper
                    src={pending}
                    onCancel={() => setPending(null)}
                    onDone={(cropped) => { onChange(cropped); setPending(null); }}
                />
            )}
        </>
    );
}

function PreviewGrid({ pages, showCaptions = true }) {
    return (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="preview-grid">
            {(pages || []).map((p) => (
                <div key={p.page_number} className="relative card-cloud overflow-hidden aspect-[3/4] booklet-noselect">
                    {p.image_url && <img src={mediaUrl(p.image_url)} className="w-full h-full object-cover" alt="" draggable={false} />}
                    <div className="absolute inset-0 watermark" />
                    {showCaptions && (
                        <div className="absolute bottom-0 left-0 right-0 p-3 bg-gradient-to-t from-ink/70 to-transparent text-white text-xs">
                            <div className="opacity-70">Page {p.page_number}</div>
                            <div className="line-clamp-2 mt-1">{p.text}</div>
                        </div>
                    )}
                </div>
            ))}
        </div>
    );
}

// Module scope: a fixed list is not state, and rebuilding it every render made
// it a phantom dependency of the interval effect below.
const LOADER_STAGES = [
    "Reading your child's little details…",
    "Writing your story…",
    "Bringing your loved ones to life…",
    "Painting page after page…",
    "Binding your keepsake…",
];

function GenerationLoader({ book, error }) {
    const [i, setI] = useState(0);
    useEffect(() => {
        const t = setInterval(() => setI((v) => (v + 1) % LOADER_STAGES.length), 2200);
        return () => clearInterval(t);
    }, []);
    const progress = book?.progress;
    return (
        <div className="max-w-md mx-auto relative">
            {/* Terry dashes across the loader and back again while it works. */}
            <div className="pointer-events-none absolute -left-[40vw] -right-[40vw] -bottom-16 h-28 hidden md:block">
                <TerryCrossing pose="run" anim="run" duration="4.2s" height={104} className="bottom-0" />
            </div>
            <motion.div animate={{ rotate: [0, 6, -6, 0] }} transition={{ duration: 6, repeat: Infinity }}
                className="w-24 h-24 mx-auto mb-8 rounded-2xl bg-lavender/50 grid place-items-center shadow-[0_20px_60px_rgba(217,108,91,0.3)]">
                <Sparkles className="w-10 h-10 text-bubblegum" />
            </motion.div>
            <div className="font-display text-3xl tracking-tight text-plum mb-4">A book is being born.</div>
            <AnimatePresence mode="wait">
                <motion.div key={i} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }}
                    className="text-plum/60" data-testid="loader-stage">
                    {LOADER_STAGES[i]}
                </motion.div>
            </AnimatePresence>
            {progress?.stage === "image" && (
                <div className="mt-6 text-sm text-plum/60">Painting page {progress.current} of {progress.total}…</div>
            )}
            {error && (
                <div className="mt-6 card-cloud p-4 text-sm text-plum/70 border-2 border-bubblegum/30"
                     data-testid="generation-error">
                    {error}
                </div>
            )}
            <div className="mt-8 h-1 rounded-full bg-border overflow-hidden">
                <motion.div animate={{ x: ["-100%", "100%"] }} transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
                    className="h-full w-1/3 bg-bubblegum" />
            </div>
        </div>
    );
}

// Payment comes before any illustration work — nothing is drawn on spec.
function Checkout({ child, cast, themes, themeKey, customTheme, artStyle, pageCount,
                    textInImage, busy, method, setMethod, onPay, onBack }) {
    const [kind, setKind] = useState("pdf");
    const [ship, setShip] = useState({ name: "", line1: "", city: "", pin: "", phone: "" });
    const themeName = themes.find((t) => t.key === themeKey)?.name || themeKey;
    const needsAddress = kind === "hardcover";
    // A courier calls before delivering, so this form asks for a phone even
    // though the server treats it as optional.
    const addrError = needsAddress
        ? firstError([validateShipping(ship), vPhone(ship.phone, false)])
        : null;
    const addressOk = !addrError;

    return (
        <div>
            <h1 className="font-display text-4xl tracking-tight mb-2">One last thing.</h1>
            <div className="script-note text-2xl mb-1">where should the hardcover go?</div>
            <p className="text-plum/60 mb-8">
                Our illustrators start on {child.name}'s book the moment this clears. It lands in your
                library within 48 hours.
            </p>

            <div className="card-cloud p-6 mb-6" data-testid="order-summary">
                <div className="font-display text-xl mb-4">Your story</div>
                <dl className="grid sm:grid-cols-2 gap-x-8 gap-y-3 text-sm">
                    <Row label="Star of the book" value={child.name} />
                    <Row label="Also appearing" value={cast.map((c) => c.name).join(", ") || "Just them"} />
                    <Row label="World" value={themeKey === "custom" ? `“${customTheme}”` : themeName} />
                    <Row label="Art style" value={artStyle} />
                    <Row label="Length" value={`${pageCount} pages`} />
                    <Row label="Words" value={textInImage ? "On the artwork" : "Captions below"} />
                </dl>
            </div>

            <div className="grid sm:grid-cols-2 gap-4 mb-6">
                {[
                    { k: "pdf", icon: BookHeart, title: "Digital storybook", price: PRICES.pdf,
                      blurb: "Print-ready PDF in your library within 48 hours. Read it anywhere, print it anywhere." },
                    { k: "hardcover", icon: Truck, title: "Printed hardcover", price: PRICES.hardcover,
                      blurb: "Everything above, plus a real hardback book posted to your door." },
                ].map((o) => (
                    <button key={o.k} onClick={() => setKind(o.k)} data-testid={`buy-${o.k}-btn`}
                        className={`card-cloud p-6 text-left transition-all ${
                            kind === o.k ? "ring-2 ring-bubblegum shadow-lg" : "hover:shadow-md opacity-80"}`}>
                        <o.icon className="w-6 h-6 text-bubblegum mb-3" />
                        <div className="font-display text-xl">{o.title}</div>
                        <div className="font-display text-3xl text-bubblegum my-2">₹{o.price}</div>
                        <p className="text-sm text-plum/60 leading-relaxed">{o.blurb}</p>
                    </button>
                ))}
            </div>

            {needsAddress && (
                <div className="card-cloud p-6 mb-6" data-testid="shipping-form">
                    <div className="font-display text-xl mb-4">Where should we post it?</div>
                    <div className="grid sm:grid-cols-2 gap-3">
                        <Input placeholder="Full name" value={ship.name} maxLength={60}
                            onChange={(e) => setShip({ ...ship, name: e.target.value })} className="bg-white" />
                        <Input placeholder="Contact phone" value={ship.phone} maxLength={20}
                            onChange={(e) => setShip({ ...ship, phone: e.target.value })} className="bg-white" />
                        <Input placeholder="Address line" value={ship.line1} maxLength={200} className="sm:col-span-2 bg-white"
                            onChange={(e) => setShip({ ...ship, line1: e.target.value })} />
                        <Input placeholder="City" value={ship.city} maxLength={120}
                            onChange={(e) => setShip({ ...ship, city: e.target.value })} className="bg-white" />
                        <Input placeholder="PIN code" value={ship.pin} inputMode="numeric" maxLength={6}
                            onChange={(e) => setShip({ ...ship, pin: e.target.value.replace(/\D/g, "") })}
                            className="bg-white" />
                    </div>
                    {addrError && Object.values(ship).some(Boolean) && (
                        <div className="text-xs text-bubblegum mt-3" data-testid="shipping-error">{addrError}</div>
                    )}
                </div>
            )}

            {/* Stacks on a phone and only becomes one row once there is width for
                it. Everything here used to be forced onto a single line. */}
            <div className="flex flex-col-reverse sm:flex-row sm:flex-wrap sm:items-center sm:justify-between gap-4">
                <Button variant="ghost" onClick={onBack} disabled={busy}
                    className="w-full sm:w-auto justify-center">
                    <ArrowLeft className="w-4 h-4 mr-1" /> Back
                </Button>
                <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4 w-full sm:w-auto">
                    <div className="flex gap-1 p-1 rounded-full bg-lavender/40 w-full sm:w-auto"
                        data-testid="method-toggle">
                        {[["razorpay", "Card / netbanking"], ["upi", "UPI"]].map(([m, label]) => (
                            <button key={m} onClick={() => setMethod(m)} data-testid={`method-${m}`}
                                className={`text-xs px-3 py-2 sm:py-1.5 rounded-full transition-colors flex-1 sm:flex-none ${
                                    method === m ? "bg-white text-plum shadow-sm" : "text-plum/60"}`}>
                                {label}
                            </button>
                        ))}
                    </div>
                    <button disabled={busy || !addressOk} data-testid="pay-btn"
                        className="btn-sticker w-full sm:w-auto order-last sm:order-none disabled:opacity-60"
                        onClick={() => onPay(kind, needsAddress ? ship : null)}>
                        {busy ? <Loader2 className="w-4 h-4 mr-1 inline animate-spin" /> : <Sparkles className="w-4 h-4 mr-1 inline" />}
                        {busy ? "Processing…" : `Pay ₹${PRICES[kind]}`}
                    </button>
                    <span className="text-xs text-plum/50 flex items-center justify-center sm:justify-start gap-1 text-center">
                        <ShieldCheck className="w-3.5 h-3.5 shrink-0" />
                        {method === "upi" ? "Pay any UPI app, confirmed by us" : "Secure checkout via Razorpay"}
                    </span>
                </div>
            </div>
        </div>
    );
}

function Row({ label, value }) {
    return (
        <div className="flex justify-between sm:block gap-4">
            <dt className="text-plum/50 text-xs uppercase tracking-wider sm:mb-1">{label}</dt>
            <dd className="font-medium capitalize text-right sm:text-left">{value}</dd>
        </div>
    );
}

function UpiPending({ childName, onLibrary }) {
    return (
        <div className="text-center py-10 max-w-lg mx-auto" data-testid="upi-pending">
            <div className="w-24 h-24 mx-auto mb-6 rounded-[2rem] bg-sky grid place-items-center
                            shadow-[0_16px_34px_rgba(45,27,78,0.18)] anim-bob">
                <ShieldCheck className="w-10 h-10 text-plum" />
            </div>
            <h1 className="font-display text-4xl tracking-tight">Checking your payment.</h1>
            <div className="script-note text-2xl mt-1 mb-4">a real person is looking at the bank</div>
            <p className="text-plum/70 leading-relaxed">
                UPI doesn't tell us the moment money lands, so someone here checks the bank by hand —
                usually within a few hours, always within 24. We'll email you the second it's confirmed,
                and that's when {childName}'s book goes to our illustrators.
            </p>
            <button onClick={onLibrary} className="btn-sticker sky mt-8">Go to my library</button>
        </div>
    );
}

function OrderPlaced({ book, childName, onLibrary }) {
    return (
        <div className="relative text-center py-10 max-w-lg mx-auto" data-testid="order-placed">
            {/* The one moment in the flow worth a little noise. */}
            <Confetti trigger={book.id} />
            <div className="w-24 h-24 mx-auto mb-6 rounded-[2rem] bg-mint grid place-items-center
                            shadow-[0_16px_34px_rgba(45,27,78,0.18)] anim-bob">
                <Check className="w-11 h-11 text-plum" strokeWidth={3} />
            </div>
            <h1 className="font-display text-4xl md:text-5xl tracking-tight">The story is yours.</h1>
            <div className="script-note text-2xl md:text-3xl mt-1">we're drawing it now</div>

            <div className="card-cloud inline-flex flex-wrap justify-center gap-8 px-8 py-5 mt-7 text-left">
                <div>
                    <div className="font-display text-[11px] tracking-[0.18em] uppercase text-plum/50">Book</div>
                    <div className="font-script text-xl text-plum leading-none mt-1">{book.title}</div>
                </div>
                <div>
                    <div className="font-display text-[11px] tracking-[0.18em] uppercase text-plum/50">Paid</div>
                    <div className="font-display text-sm text-plum mt-1">₹{PRICES[book.purchase_kind] ?? PRICES.pdf}</div>
                </div>
                <div>
                    <div className="font-display text-[11px] tracking-[0.18em] uppercase text-plum/50">
                        {book.purchase_kind === "hardcover" ? "Hardcover" : "Delivery"}
                    </div>
                    <div className="font-display text-sm text-plum mt-1">
                        {book.purchase_kind === "hardcover" ? "Posted after approval" : "In 48 hours"}
                    </div>
                </div>
            </div>

            <p className="text-plum/70 leading-relaxed mt-7">
                {childName}'s book is with our illustrators now. You'll get an email the moment it's
                ready — usually within 48 hours — and it'll be waiting in your library.
            </p>
            {book.purchase_kind === "hardcover" && (
                <p className="text-plum/60 text-sm mt-3">
                    Your printed copy ships once the artwork is approved. We'll send tracking to the same address.
                </p>
            )}
            <button onClick={onLibrary} data-testid="go-library-btn" className="btn-sticker mt-8">
                Go to my bookshelf <ArrowRight className="w-4 h-4 ml-1 inline" />
            </button>
        </div>
    );
}
